import { flagValuesFrom, type FlagValues } from '@platform/flags';
import { z } from 'zod';

const base = z.object({
  VITE_KEYCLOAK_URL: z.url(),
  VITE_KEYCLOAK_REALM: z.string().min(1),
  VITE_KEYCLOAK_CLIENT_ID: z.string().min(1),
  VITE_API_BASE_URL: z.url(),
  VITE_SHIPPING_API_BASE_URL: z.url(),
  VITE_APP_HOST: z.string().min(1),
  VITE_DEFAULT_LOCALE: z.enum(['fr', 'ar', 'en']),
  VITE_FEATURE_FLAGS: z.string().transform((raw, context) => {
    const values = flagValuesFrom(raw);
    if (values === undefined)
      context.addIssue({ code: 'custom', message: 'every registered flag as a boolean' });
    return values ?? z.NEVER;
  }),
});

const support = z.discriminatedUnion('VITE_SUPPORT_MODE', [
  z.object({ VITE_SUPPORT_MODE: z.literal('none') }),
  z.object({
    VITE_SUPPORT_MODE: z.literal('links'),
    VITE_SUPPORT_WHATSAPP_URL: z.url(),
    VITE_SUPPORT_GUIDES_URL: z.url(),
    VITE_SUPPORT_HELP_CENTER_URL: z.url(),
  }),
]);

const schema = z.intersection(base, support);

export type SupportLinks =
  | { readonly mode: 'none' }
  | {
      readonly mode: 'links';
      readonly whatsappUrl: string;
      readonly guidesUrl: string;
      readonly helpCenterUrl: string;
    };

export type AppConfig = {
  keycloakUrl: string;
  keycloakRealm: string;
  keycloakClientId: string;
  apiBaseUrl: string;
  shippingApiBaseUrl: string;
  appHost: string;
  defaultLocale: 'fr' | 'ar' | 'en';
  support: SupportLinks;
  flags: FlagValues;
};

export class ConfigError extends Error {
  readonly keys: readonly string[];

  constructor(keys: readonly string[]) {
    super(`Invalid or missing configuration: ${keys.join(', ')}`);
    this.name = 'ConfigError';
    this.keys = keys;
  }
}

export const parseConfig = (raw: Record<string, unknown>): AppConfig => {
  const result = schema.safeParse(raw);
  if (!result.success) {
    const keys = [...new Set(result.error.issues.map((issue) => issue.path.map(String).join('.')))];
    throw new ConfigError(keys);
  }
  const values = result.data;
  return {
    keycloakUrl: values.VITE_KEYCLOAK_URL.replace(/\/$/, ''),
    keycloakRealm: values.VITE_KEYCLOAK_REALM,
    keycloakClientId: values.VITE_KEYCLOAK_CLIENT_ID,
    apiBaseUrl: values.VITE_API_BASE_URL,
    shippingApiBaseUrl: values.VITE_SHIPPING_API_BASE_URL,
    appHost: values.VITE_APP_HOST,
    defaultLocale: values.VITE_DEFAULT_LOCALE,
    flags: values.VITE_FEATURE_FLAGS,
    support:
      values.VITE_SUPPORT_MODE === 'links'
        ? {
            mode: 'links',
            whatsappUrl: values.VITE_SUPPORT_WHATSAPP_URL,
            guidesUrl: values.VITE_SUPPORT_GUIDES_URL,
            helpCenterUrl: values.VITE_SUPPORT_HELP_CENTER_URL,
          }
        : { mode: 'none' },
  };
};

export const loadConfig = (): AppConfig => parseConfig({ ...import.meta.env, ...(window.__ENV__ ?? {}) });
